// src/components/sections/HomeTabsSection.jsx
import React, { useState, useRef } from 'react'
import { motion as Motion, useInView, AnimatePresence } from 'framer-motion'
import { FiCheckCircle } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import './HomeTabsSection.scss'

export default function HomeTabsSection({ sectionData }) {
    const [activeTab, setActiveTab] = useState(0)
    const sectionRef = useRef(null)
    const isInView = useInView(sectionRef, { amount: 0.3 })

    const navigate = useNavigate()

    // Skeleton if no section data yet
    if (!sectionData) {
        return (
            <section className="services-tabs" ref={sectionRef}>
                <div className="container">
                    <div className="animate-pulse">
                        <div className="h-8 bg-black/10 rounded w-1/3 mx-auto mb-8" />
                        <div className="h-10 bg-black/10 rounded w-2/3 mx-auto mb-8" />
                        <div className="h-64 bg-black/10 rounded-lg mb-4" />
                    </div>
                </div>
            </section>
        )
    }

    const { heading, tabs = [] } = sectionData

    if (!tabs.length) return null

    const tabsData = tabs

    const containerVariants = {
        hidden: { opacity: 0, y: 40 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: 'easeOut' },
        },
        exit: { opacity: 0, y: -20 },
    }

    // Same "item" feel if needed later
    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: (i) => ({
            opacity: 1,
            y: 0,
            transition: { delay: i * 0.1, duration: 0.5 },
        }),
    }

    return (
        <section className="services-tabs" ref={sectionRef}>
            <div className="container">
                {/* Heading */}
                {heading && (
                    <Motion.h2
                        className="section-heading"
                        initial="hidden"
                        animate={isInView ? 'visible' : 'hidden'}
                        variants={containerVariants}
                    >
                        {heading.toUpperCase()}
                    </Motion.h2>
                )}

                {/* Tabs header */}
                <Motion.div
                    className="tabs-header-parent"
                    initial="hidden"
                    animate={isInView ? 'visible' : 'hidden'}
                    variants={containerVariants}
                    transition={{ delay: 0.1 }}
                >
                    <div className="tabs-header">
                        {tabsData.map((tab, index) => (
                            <button
                                key={index}
                                className={`tab-button ${activeTab === index ? 'active' : ''}`}
                                onClick={() => setActiveTab(index)}
                            >
                                {tab.title || tab.heading || `Tab ${index + 1}`}
                            </button>
                        ))}
                    </div>
                </Motion.div>

                {/* Tab content */}
                <AnimatePresence mode="wait">
                    {tabsData.map((tab, index) =>
                        activeTab === index ? (
                            <Motion.div
                                key={index}
                                className="tab-content"
                                initial="hidden"
                                animate={isInView ? 'visible' : 'hidden'}
                                exit="exit"
                                variants={containerVariants}
                                transition={{ delay: 0.2 }}
                            >
                                {/* Left: Image */}
                                <div className="content-image">
                                    {tab.imageUrl && (
                                        <img
                                            src={tab.imageUrl}
                                            alt={tab.heading || tab.title}
                                            loading="lazy"
                                        />
                                    )}
                                </div>

                                {/* Right: Details */}
                                <div className="content-details">
                                    <Motion.h3 variants={containerVariants}>
                                        {tab.heading || tab.title}
                                    </Motion.h3>

                                    {tab.subheading && (
                                        <Motion.p
                                            className="subheading"
                                            variants={containerVariants}
                                            custom={0.5}
                                        >
                                            {tab.subheading}
                                        </Motion.p>
                                    )}

                                    {/* Features / spans */}
                                    {Array.isArray(tab.spans) && tab.spans.length > 0 && (
                                        <div className="features-grid">
                                            {tab.spans.map((span, i) => (
                                                <Motion.div
                                                    key={i}
                                                    className="feature-item"
                                                    variants={itemVariants}
                                                    custom={i}
                                                >
                                                    <span className="feature-icon">
                                                        <FiCheckCircle />
                                                    </span>
                                                    <p>{span}</p>
                                                </Motion.div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Link / CTA */}
                                    <Motion.p
                                        className="content-link"
                                        variants={containerVariants}
                                        custom={1}
                                        onClick={() => {
                                            const link = tab.buttonLink || '/contact'
                                            navigate(link)
                                        }}
                                    >
                                        <span>{tab.buttonText || 'Learn more'}</span>
                                        <span className="underline" />
                                    </Motion.p>
                                </div>
                            </Motion.div>
                        ) : null
                    )}
                </AnimatePresence>
            </div>
        </section>
    )
}